import { ReactElement, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import {
  Checkbox,
  FormControlLabel,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { CardTitle, Modal } from 'components'
import { TFlourAttributeGroup, TFlourAttributeItem } from 'types/models/Flours'
import { successToast } from 'utils/toastService'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useAnalyticsServices } from '../pages/services/services'

interface IUseFiltersModalProps {
  userAttributeFilters: TFlourAttributeGroup[]
  post_aging: boolean
  isBakerModule: boolean
}

const useFiltersModal = ({
  userAttributeFilters,
  post_aging,
  isBakerModule,
}: IUseFiltersModalProps): [ReactElement, VoidFunction] => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [addFilters, setAddFilters] = useState<number[]>([])
  const [removeFilters, setRemoveFilters] = useState<number[]>([])

  const { addUserAttributeFilters, removeUserAttributeFilters } =
    useAnalyticsServices(isBakerModule)
  const toggleModal = () => setIsOpen(!isOpen)
  const theme = useTheme()
  const isXXSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const queryClient = useQueryClient()
  const profile = useSelector((state: RootState) => state.user.profile)

  const allNonMandatoryIds = Array.isArray(userAttributeFilters)
    ? userAttributeFilters
        .flatMap((group) => group.attributes)
        .filter(
          (attr) =>
            !attr.is_mandatory &&
            ((attr.range_max && attr.range_max !== 0) ||
              (attr.range_min && attr.range_min !== 0))
        )
        .map((attr) => attr.id)
    : []

  const isAllSelected = allNonMandatoryIds.every((id) =>
    addFilters.includes(id)
  )

  useEffect(() => {
    if (!isOpen || !Array.isArray(userAttributeFilters)) return

    const mandatoryIds = userAttributeFilters
      .flatMap((group) => group.attributes)
      .filter((attr) => attr.is_mandatory)
      .map((attr) => attr.id)

    const checkedIds = userAttributeFilters
      .flatMap((group) => group.attributes)
      .filter(
        (attr) =>
          attr.checked &&
          ((attr.range_max && attr.range_max !== 0) ||
            (attr.range_min && attr.range_min !== 0))
      )
      .map((attr) => attr.id)

    setAddFilters(
      [...mandatoryIds, ...checkedIds].filter(
        (id, index, self) => self.indexOf(id) === index
      )
    )
    setRemoveFilters([])
  }, [userAttributeFilters, isOpen])

  const handleOnCheckClick = (
    checked: boolean,
    attribute: TFlourAttributeItem
  ) => {
    // Block toggling of mandatory attributes
    if (attribute.is_mandatory) return

    if (checked) {
      setAddFilters((prev) =>
        prev.includes(attribute.id) ? prev : [...prev, attribute.id]
      )
      setRemoveFilters((prev) => prev.filter((id) => id !== attribute.id))
    } else {
      setRemoveFilters((prev) =>
        prev.includes(attribute.id) ? prev : [...prev, attribute.id]
      )
      setAddFilters((prev) => prev.filter((id) => id !== attribute.id))
    }
  }

  const handleToggleAll = () => {
    if (isAllSelected) {
      // Uncheck all non-mandatory
      setAddFilters((prev) =>
        prev.filter((id) => !allNonMandatoryIds.includes(id))
      )
      setRemoveFilters((prev) => [
        ...removeFilters,
        ...allNonMandatoryIds.filter((id) => !removeFilters.includes(id)),
      ])
    } else {
      // Check all non-mandatory
      setAddFilters((prev) => [
        ...prev,
        ...allNonMandatoryIds.filter((id) => !prev.includes(id)),
      ])
      setRemoveFilters((prev) =>
        prev.filter((id) => !allNonMandatoryIds.includes(id))
      )
    }
  }

  const { mutate: mutateAdd } = useMutation({
    mutationFn: addUserAttributeFilters,
    onSuccess: (res) => {
      successToast(
        `Filters for user ${profile.name} have been successfully updated`
      )
      void queryClient.invalidateQueries(['delivery-by-id'])
      void queryClient.invalidateQueries(['flour_attributes_user_filter'])
    },
    onError: (err) => {
      toast.error(err.toString())
    },
  })

  const { mutate: mutateRemove } = useMutation({
    mutationFn: removeUserAttributeFilters,
    onSuccess: (res) => {
      successToast(
        `Filters for user ${profile.name} have been successfully removed`
      )
      void queryClient.invalidateQueries(['delivery-by-id'])
      void queryClient.invalidateQueries(['flour_attributes_user_filter'])
    },
    onError: (err) => {
      toast.error(err.toString())
    },
  })

  const onSubmit = () => {
    const add = addFilters.filter(
      (id) =>
        !userAttributeFilters
          .flatMap((group) => group.attributes)
          .find((attr) => attr.id === id)?.is_mandatory
    )

    const remove = removeFilters.filter(
      (id) =>
        !userAttributeFilters
          .flatMap((group) => group.attributes)
          .find((attr) => attr.id === id)?.is_mandatory
    )

    if (add.length > 0) {
      mutateAdd({
        attribute_ids: add,
        post_aging: post_aging,
      })
    }

    if (remove.length > 0) {
      mutateRemove({
        attribute_ids: remove,
        post_aging: post_aging,
      })
    }

    toggleModal()
  }

  const isAttributeDisabled = (attribute: TFlourAttributeItem) => {
    return (
      attribute.is_mandatory ||
      ((attribute.range_min === 0 ||
        attribute.range_min === null ||
        attribute.range_min === undefined) &&
        (attribute.range_max === 0 ||
          attribute.range_max === null ||
          attribute.range_max === undefined))
    )
  }

  const determineTooltip = (attribute: TFlourAttributeItem) => {
    return attribute.is_mandatory
      ? 'Mandatory field: cannot be unchecked'
      : 'Disabled: missing min and max range values'
  }

  const checkBoxElement = (attribute: TFlourAttributeItem) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={
            (attribute.is_mandatory &&
              (attribute.range_min === 0 ||
                attribute.range_min === null ||
                attribute.range_min === undefined) &&
              (attribute.range_max === 0 ||
                attribute.range_max === null ||
                attribute.range_max === undefined)) ||
            addFilters.includes(attribute.id)
          }
          disabled={
            attribute.is_mandatory ||
            ((attribute.range_min === 0 ||
              attribute.range_min === null ||
              attribute.range_min === undefined) &&
              (attribute.range_max === 0 ||
                attribute.range_max === null ||
                attribute.range_max === undefined))
          }
          onChange={() =>
            handleOnCheckClick(!addFilters.includes(attribute.id), attribute)
          }
        />
      }
      label={attribute.name}
    />
  )

  return [
    <Modal
      isOpen={isOpen}
      onClose={toggleModal}
      title={'Filter Display'}
      onConfirm={onSubmit}
    >
      <Box px="30px" py="20px" width="100%">
        <Box>
          <FormControlLabel
            control={
              <Checkbox checked={isAllSelected} onChange={handleToggleAll} />
            }
            label="All"
          />
        </Box>
        <Grid container spacing={1}>
          {userAttributeFilters?.map((category) => (
            <Grid size={isXXSmallScreen ? 12 : 6} key={category.id}>
              <CardTitle title={category.name} />
              {category.attributes.map((attribute) => (
                <Box key={attribute.id}>
                  {isAttributeDisabled(attribute) ? (
                    <Tooltip
                      title={determineTooltip(attribute)}
                      placement="top"
                    >
                      <Box display="inline-block">
                        {checkBoxElement(attribute)}
                      </Box>
                    </Tooltip>
                  ) : (
                    checkBoxElement(attribute)
                  )}
                </Box>
              ))}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>,
    toggleModal,
  ]
}

export default useFiltersModal
